'use client';

import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash-es';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { UniversalSearchCategory } from '@/app/(pages)/results/constants';
import {
  capacityAdultAtom,
  capacityChildAgesAtom,
  capacityChildAtom,
  checkInDateAtom,
  checkOutDateAtom,
} from '@/app/_shared/store/local-accommodation';
import { globalCheckIOCapacityAtom, setGlobalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';
import { selectedVerticalTabInfoAtom } from '@/app/_shared/store/universal-search';
import { updateSessionLocalFilters } from '@/app/_shared/api';

interface SyncWebViewSessionProps {
  category?: keyof typeof UniversalSearchCategory;
  searchParams?: Record<string, string>;
}

export const useSyncWebViewSession = ({ category, searchParams }: SyncWebViewSessionProps) => {
  const [checkInDate, setCheckInDate] = useAtom(checkInDateAtom);
  const [checkOutDate, setCheckOutDate] = useAtom(checkOutDateAtom);
  const [capacityAdult, setCapacityAdult] = useAtom(capacityAdultAtom);
  const [capacityChild, setCapacityChild] = useAtom(capacityChildAtom);
  const [capacityChildAges, setCapacityChildAges] = useAtom(capacityChildAgesAtom);
  const selectedVerticalTabInfo = useAtomValue(selectedVerticalTabInfoAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);
  const setGlobalCheckIOCapacity = useSetAtom(setGlobalCheckIOCapacityAtom);

  const selectedVerticalTabInfoRef = useRef(selectedVerticalTabInfo);
  const checkInDateRef = useRef(checkInDate);
  const checkOutDateRef = useRef(checkOutDate);
  const capacityAdultRef = useRef(capacityAdult);
  const capacityChildRef = useRef(capacityChild);
  const capacityChildAgesRef = useRef(capacityChildAges);
  const globalCheckInDateRef = useRef(globalCheckIOCapacity.checkInDate);
  const globalCheckOutDateRef = useRef(globalCheckIOCapacity.checkOutDate);
  const globalCapacityRoomsRef = useRef(globalCheckIOCapacity.capacityRooms);

  useEffect(() => {
    selectedVerticalTabInfoRef.current = selectedVerticalTabInfo;
    checkInDateRef.current = checkInDate;
    checkOutDateRef.current = checkOutDate;
    capacityAdultRef.current = capacityAdult;
    capacityChildRef.current = capacityChild;
    capacityChildAgesRef.current = capacityChildAges;
    globalCheckInDateRef.current = globalCheckIOCapacity.checkInDate;
    globalCheckOutDateRef.current = globalCheckIOCapacity.checkOutDate;
    globalCapacityRoomsRef.current = globalCheckIOCapacity.capacityRooms;
  }, [
    selectedVerticalTabInfo,
    checkInDate,
    checkOutDate,
    capacityAdult,
    capacityChild,
    capacityChildAges,
    globalCheckIOCapacity.checkInDate,
    globalCheckIOCapacity.checkOutDate,
    globalCheckIOCapacity.capacityRooms,
  ]);

  useEffect(() => {
    if (!yaNative.isEnabled) return;

    // 최초 진입시 앱 날짜/인원 동기화
    // 쿼리 파라미터로 전달된 카테고리는 전달된 값을 기준으로 앱 필터를 설정하고 웹 세션 상태는 서버사이드에서 동기화 했으므로 동기화하지 않음
    // checkInDate, checkOutDate 있음 => 쿼리 기준으로 앱 설정
    // checkInDate, checkOutDate 없음 => 앱 기준으로 웹 세션 상태 동기화
    if (category === UniversalSearchCategory.LOCAL_ACCOMMODATION) {
      if (searchParams?.checkInDate && searchParams?.checkOutDate) {
        yaNative.setFilter({
          type: 'domestic',
          hasToast: false,
          checkDate: { in: checkInDate || '', out: checkOutDate || '' },
          rooms: capacityAdult ? [{ adult: capacityAdult, childages: capacityChildAges }] : [],
        });
      } else {
        syncNativeLocalState(true);
      }
    }

    if (category === UniversalSearchCategory.GLOBAL_ACCOMMODATION) {
      if (searchParams?.checkInDate && searchParams?.checkOutDate) {
        yaNative.setFilter({
          type: 'overseas',
          hasToast: false,
          checkDate: { in: globalCheckIOCapacity.checkInDate || '', out: globalCheckIOCapacity.checkOutDate || '' },
          rooms: globalCheckIOCapacity.capacityRooms.map((room) => ({
            adult: room.adult,
            childages: room.child,
          })),
        });
      } else {
        syncNativeGlobalState(true);
      }
    }

    // 화면 전환 이벤트 발생시 앱 날짜/인원 정보 동기화
    const handleVisibilityChange = async () => {
      if (yaNative.isEnabled && !document.hidden) {
        const key = selectedVerticalTabInfoRef.current?.key;
        if (key === 'LOCAL_ACCOMMODATION') {
          syncNativeLocalState();
        }

        if (key === 'GLOBAL_ACCOMMODATION') {
          syncNativeGlobalState();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const syncNativeLocalState = (update = false) => {
    if (!yaNative.isEnabled) return;

    yaNative.filter({ type: 'domestic' }, (success, info) => {
      if (!success || !info) return;

      const nativeCheckInOutCapacity = {
        checkInDate: info.checkDate?.in,
        checkOutDate: info.checkDate?.out,
        capacityAdult: info.rooms?.[0]?.adult,
        capacityChild: info.rooms?.[0]?.childages?.length || 0,
        capacityChildAges: info.rooms?.[0]?.childages || [],
      };

      const localData = {
        checkInDate: checkInDateRef.current,
        checkOutDate: checkOutDateRef.current,
        capacityAdult: capacityAdultRef.current,
        capacityChild: capacityChildRef.current || 0,
        capacityChildAges: capacityChildAgesRef.current || [],
      };

      if (isEqual(nativeCheckInOutCapacity, localData)) {
        return;
      }

      if (update) {
        updateSessionLocalFilters({
          checkinDate: nativeCheckInOutCapacity.checkInDate,
          checkoutDate: nativeCheckInOutCapacity.checkOutDate,
          capacityAdult: nativeCheckInOutCapacity.capacityAdult,
          capacityChild: nativeCheckInOutCapacity.capacityChild,
          capacityChildAges: nativeCheckInOutCapacity.capacityChildAges,
          prevCheckinDate: nativeCheckInOutCapacity?.checkInDate,
          prevCheckoutDate: nativeCheckInOutCapacity?.checkOutDate,
        });
      }

      setCheckInDate(nativeCheckInOutCapacity.checkInDate);
      setCheckOutDate(nativeCheckInOutCapacity.checkOutDate);
      setCapacityAdult(nativeCheckInOutCapacity.capacityAdult);
      setCapacityChild(nativeCheckInOutCapacity.capacityChild);
      setCapacityChildAges(nativeCheckInOutCapacity.capacityChildAges);

      window.scrollTo(0, 0);
    });
  };

  const syncNativeGlobalState = (update = false) => {
    if (!yaNative.isEnabled) return;

    yaNative.filter({ type: 'overseas' }, (success, info) => {
      if (!success || !info) return;

      const nativeGlobalCheckInOutCapacity = {
        checkInDate: info.checkDate?.in,
        checkOutDate: info.checkDate?.out,
        globalCapacityRooms: info?.rooms?.map((room) => ({
          adult: room.adult,
          child: room.childages || [],
        })),
      };

      const globalData = {
        checkInDate: globalCheckInDateRef.current,
        checkOutDate: globalCheckOutDateRef.current,
        globalCapacityRooms: globalCapacityRoomsRef.current,
      };

      if (isEqual(nativeGlobalCheckInOutCapacity, globalData)) {
        return;
      }

      if (update) {
        updateSessionLocalFilters({
          globalPlaceFilter: {
            checkinDate: nativeGlobalCheckInOutCapacity.checkInDate,
            checkoutDate: nativeGlobalCheckInOutCapacity.checkOutDate,
            rooms: nativeGlobalCheckInOutCapacity.globalCapacityRooms,
          },
          globalPrevCheckinDate: nativeGlobalCheckInOutCapacity.checkInDate,
          globalPrevCheckoutDate: nativeGlobalCheckInOutCapacity.checkOutDate,
        });
      }

      setGlobalCheckIOCapacity({
        checkInDate: nativeGlobalCheckInOutCapacity.checkInDate,
        checkOutDate: nativeGlobalCheckInOutCapacity.checkOutDate,
        capacityRooms: nativeGlobalCheckInOutCapacity.globalCapacityRooms,
      });

      window.scrollTo(0, 0);
    });
  };
};
